import { useQuery } from "@apollo/client/react";
import type { DeepPartial } from "@apollo/client/utilities";
import Link from "next/link";
import { use } from "react";
import {
  LuChartLine as LuLineChart,
  LuExternalLink,
  LuZap,
} from "react-icons/lu";
import { useLocalStorage } from "usehooks-ts";

import { DIALOG, setDialog } from "@/components/app/dialogs/dialog-provider";
import CoinPriceChangePercentageFormatter from "@/components/shared/coin-price-change-percentage-formatter";
import CoinPriceFormatter from "@/components/shared/coin-price-formatter";
import { CreatorCoinTab } from "@/components/shared/creator-coin-exchange-modal/creator-coin-tabs";
import DesoAvatar from "@/components/shared/deso-avatar";
import { TriggerLoggedInModalWindow } from "@/components/shared/is-logged-in";
import { Metric } from "@/components/shared/metric";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { UserContext } from "@/contexts/user-context";
import type {
  DaoCoinStat,
  TokenMarketVolumeSummary,
} from "@/graphql/codegen/graphql";
import {
  DaoCoinStatsDocument,
  DaoCoinStatsOrderBy,
} from "@/graphql/codegen/graphql";
import type { CoinPropertiesResponse } from "@/hooks/use-coin-properties-query";
import { useTokenMarketData } from "@/hooks/use-token-market-data";
import { useTokenOrderbook } from "@/hooks/use-token-orderbook";
import {
  APP_NAME_GENERIC,
  FOCUS_TICKER_DISPLAY_NAME,
  FOCUS_TOKEN_DISPLAY_NAME,
  FOCUS_TOKEN_SUPPLY,
  MIN_DESO_TOKEN_PCT_CHANGE_TO_HIGHLIGHT_TRENDING,
  OPENFUND_APP_NAME,
} from "@/utils/constants";
import { centsToUSD, getVisiblePrice, usdToFocus } from "@/utils/currency";
import { abbreviateNumber } from "@/utils/helpers";
import { cn } from "@/utils/lib";
import { LocalStorageKeys } from "@/utils/local-storage-keys";
import { getOrderByKey } from "@/utils/trade";
import { getUserName } from "@/utils/usernames";

import { AdvancedTradingViewChartContainer } from "../chart/advanced-trading-view-chart-container";
import { LearnMore } from "../learn-more-link";
import TokenOrderbook, { OrderSide } from "../trade/amm/token-orderbook";
import type { TrendingTabSelection } from "../trade/trending-coins/constants";
import { trendingTabs } from "../trade/trending-coins/constants";

interface TokenPostLaunchOverviewProps {
  tokenDetails: CoinPropertiesResponse;
  publicKey: string;
  focusExchangeRate: number;
}

export const TokenPostLaunchOverview = ({
  tokenDetails,
  publicKey,
  focusExchangeRate,
}: TokenPostLaunchOverviewProps) => {
  const [timeFilter, setTimeFilter] = useLocalStorage<TrendingTabSelection>(
    LocalStorageKeys.tokenPostLaunchOverviewTimeFilter,
    trendingTabs[1],
  );
  const { currentUserPublicKey } = use(UserContext);

  const { data: tokenMarketData, loading: loadingTokenMarketData } =
    useTokenMarketData({
      publicKey,
      ammPublicKey: tokenDetails.coinProperties.AmmConfigs?.[0]?.AmmPublicKey,
      currentUserPublicKey,
    });

  const username = getUserName({
    account: {
      publicKey,
      username: tokenDetails.coinProperties.Profile.Username,
      extraData: tokenDetails.coinProperties.Profile.ExtraData,
    },
    customPrefix: "$",
  });

  const tokenPriceUsd = tokenDetails?.coinPrice
    ? getVisiblePrice(tokenDetails.coinPrice)
    : 0;

  const { tokenOrderbook, loading: loadingTokenOrderbook } = useTokenOrderbook({
    baseCurrencyPublicKeyBase58Check:
      tokenDetails.coinProperties.AmmConfigs?.[0].BaseCurrencyPkid || publicKey,
    quoteCurrencyPublicKeyBase58Check:
      tokenDetails.coinProperties.AmmConfigs?.[0].QuoteCurrencyPkid ||
      process.env.NEXT_PUBLIC_FOCUS_APP_PUBLIC_KEY,
  });

  const { data: trendingData, loading: loadingTrendingCoins } = useQuery(
    DaoCoinStatsDocument,
    {
      fetchPolicy: "network-only",
      variables: {
        orderBy: getOrderByKey(
          [{ id: timeFilter.orderBy, desc: true }],
          DaoCoinStatsOrderBy,
          DaoCoinStatsOrderBy.VolumeUsdPast_24HDesc,
        ),
        filter: {
          coinUsername: {
            equalTo: tokenDetails.coinProperties.Profile.Username,
          },
        },
        first: 1,
        offset: 0,
      },
    },
  );
  const coinData =
    (trendingData?.daoCoinStats?.nodes?.[0] as DaoCoinStat | undefined) ?? null;

  const tokenSummary =
    tokenMarketData?.tokenMarketVolumeSummaries?.nodes[0] ?? null;
  const metricsLoading = loadingTrendingCoins || loadingTokenMarketData;

  return (
    <div>
      <div
        className="
          flex flex-col gap-6
          sm:flex-row sm:items-center sm:justify-between sm:gap-2
        "
      >
        <div className="flex flex-row items-center gap-3">
          <DesoAvatar
            size={40}
            account={{
              publicKey:
                tokenDetails.coinProperties.Profile.PublicKeyBase58Check,
              username: tokenDetails.coinProperties.Profile.Username,
              extraData: tokenDetails.coinProperties.Profile.ExtraData,
            }}
          />
          <div className="flex flex-col">
            <div className="flex items-center gap-1 text-muted-foreground">
              <span className="font-sans font-semibold">{username}</span>
              <span className="text-xs text-muted">
                / {FOCUS_TOKEN_DISPLAY_NAME}
              </span>
            </div>
            <Link
              href={`${process.env.NEXT_PUBLIC_OPENFUND_URI}/trade/${tokenDetails.coinProperties.Profile.Username}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              <div
                className="
                  flex cursor-pointer items-center gap-1 text-xs text-muted
                  underline-offset-2
                  hover:underline
                "
              >
                Trade on {OPENFUND_APP_NAME}
                <LuExternalLink className="text-sm text-muted" />
              </div>
            </Link>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {trendingTabs.map((tab) => (
            <Button
              key={tab.orderBy}
              variant={
                timeFilter.orderBy === tab.orderBy ? "default" : "outline"
              }
              size="sm"
              onClick={() => {
                setTimeFilter(tab);
              }}
              disabled={loadingTrendingCoins}
            >
              {tab.timeDuration}
            </Button>
          ))}
        </div>
      </div>
      <PostLaunchOverviewTokenMetrics
        {...{
          metricsLoading,
          timeFilter,
          coinData,
          tokenSummary,
          tokenPriceUsd,
          focusExchangeRate,
          username,
          publicKey,
        }}
      />
      <div className="mt-4 w-full" />
      <AdvancedTradingViewChartContainer
        publicKey={publicKey}
        username={tokenDetails.coinProperties.Profile.Username}
      />
      <div
        className="
          mt-4 mb-0 grid grid-cols-1 gap-4 text-center
          md:grid-cols-2
        "
      >
        <div className="rounded-xl border p-6">
          <h3
            className="
              m-auto mb-2 flex w-full items-center justify-center gap-2
              font-sans text-lg font-semibold
            "
          >
            <LuZap className="text-lg text-muted" />
            Quick Buy
          </h3>
          <p className="mb-4 text-sm text-balance text-muted">
            Buy &amp; sell {username} tokens directly on {APP_NAME_GENERIC}.
          </p>
          <TriggerLoggedInModalWindow>
            <Button
              onClick={() =>
                setDialog(DIALOG.TradeCoin, {
                  open: true,
                  params: {
                    publicKey,
                    tab: CreatorCoinTab.Buy,
                  },
                })
              }
            >
              Quick Buy
            </Button>
          </TriggerLoggedInModalWindow>
        </div>
        <div className="rounded-xl border p-6">
          <h3
            className="
              m-auto mb-2 flex w-full items-center justify-center gap-2
              font-sans text-lg font-semibold
            "
          >
            <LuLineChart className="text-lg text-muted" />
            Advanced Trade
          </h3>
          <p className="mb-4 text-sm text-balance text-muted">
            Set limit orders and access deeper liquidity on {OPENFUND_APP_NAME}.
          </p>
          <Link
            href={`${process.env.NEXT_PUBLIC_OPENFUND_URI}/trade/${tokenDetails.coinProperties.Profile.Username}`}
            target="_blank"
          >
            <Button className="m-auto flex items-center justify-center gap-2">
              Trade on {OPENFUND_APP_NAME}
              <LuExternalLink className="text-sm" />
            </Button>
          </Link>
        </div>
      </div>
      <div className="w-full">
        <div className="mb-4 flex items-center justify-center">
          <LearnMore />
        </div>

        <div
          className="
            mb-4 flex items-center gap-1 font-sans font-semibold
            text-muted-foreground
          "
        >
          Orderbook
        </div>

        {loadingTokenOrderbook ? (
          <div className="flex flex-col gap-2">
            <Skeleton className="h-[400px] w-full" />
            <Skeleton className="h-[400px] w-full" />
          </div>
        ) : (
          <div className="space-y-2">
            <TokenOrderbook
              orders={tokenOrderbook?.asks || []}
              coinUsername={username}
              coinPublicKey={publicKey}
            />

            <div className="my-4 w-full text-center text-sm text-muted">
              Mid Price:{" "}
              <span className="font-mono text-muted-foreground">
                <CoinPriceFormatter
                  price={tokenPriceUsd}
                  className="font-mono text-sm text-muted-foreground"
                />
              </span>
            </div>

            <TokenOrderbook
              showTitles={false}
              orders={tokenOrderbook?.bids || []}
              coinUsername={username}
              coinPublicKey={publicKey}
              side={OrderSide.BID}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export const PostLaunchOverviewTokenMetrics = ({
  metricsLoading,
  timeFilter = trendingTabs[1],
  coinData,
  tokenPriceUsd,
  focusExchangeRate = 0,
  tokenSummary,
  username,
  publicKey,
}: {
  metricsLoading: boolean;
  timeFilter?: TrendingTabSelection;
  coinData?: Partial<DaoCoinStat> | null;
  tokenPriceUsd: number;
  tokenSummary?: DeepPartial<TokenMarketVolumeSummary> | null;
  focusExchangeRate?: number;
  username: string;
  publicKey: string;
}) => {
  const volumeUsd =
    coinData?.[timeFilter.volumeAccessorKey as keyof DaoCoinStat] ?? 0;
  const priceChangePct =
    coinData?.[timeFilter.priceChangeAccessorKey as keyof DaoCoinStat] ?? 0;
  const isFocusToken =
    publicKey === process.env.NEXT_PUBLIC_FOCUS_APP_PUBLIC_KEY;

  const marketCapUsd = centsToUSD(
    Number(
      isFocusToken
        ? tokenPriceUsd * FOCUS_TOKEN_SUPPLY * 100
        : (tokenSummary?.marketCapUsdCents ?? "0"),
    ),
  );
  const trendingColor =
    priceChangePct >= 0 &&
    priceChangePct < MIN_DESO_TOKEN_PCT_CHANGE_TO_HIGHLIGHT_TRENDING
      ? "text-muted"
      : priceChangePct >= MIN_DESO_TOKEN_PCT_CHANGE_TO_HIGHLIGHT_TRENDING
        ? "text-emerald-400"
        : "text-red-600";

  return (
    <div className="mt-4 flex flex-col">
      {metricsLoading ? (
        <div
          className="
            grid grid-cols-1 gap-4
            md:grid-cols-4
          "
        >
          {[...Array(4)].map((_, i) => (
            <div
              key={i}
              className="rounded-xl border border-border bg-background p-4"
            >
              <Skeleton className="mb-2 h-4 w-3/4" />
              <Skeleton className="mb-2.5 h-[22px] w-full" />
              <Skeleton className="h-4 w-1/2" />
            </div>
          ))}
        </div>
      ) : (
        <div className="flex flex-wrap gap-4">
          <div className="min-w-[150px] grow basis-[150px]">
            <Metric
              state="inActive"
              label={`Token Price (${timeFilter.timeDuration})`}
              key="tokenPrice"
              value={
                <CoinPriceFormatter price={tokenPriceUsd} className="text-lg" />
              }
              caption={`${abbreviateNumber(usdToFocus(tokenPriceUsd, focusExchangeRate))} ${FOCUS_TICKER_DISPLAY_NAME}`}
              valueClassName={cn("leading-6!", trendingColor)}
              className="border border-border bg-background"
            />
          </div>
          {!!volumeUsd && (
            <div className="min-w-[150px] grow basis-[150px]">
              <Metric
                state="inActive"
                label={`Volume (${timeFilter.timeDuration})`}
                key="volume"
                value={
                  <CoinPriceFormatter price={volumeUsd} className="text-lg" />
                }
                caption={`${abbreviateNumber(usdToFocus(volumeUsd, focusExchangeRate))} ${username}`}
                valueClassName={cn("leading-6!", trendingColor)}
                className="border border-border bg-background"
              />
            </div>
          )}
          <div className="min-w-[150px] grow basis-[150px]">
            <Metric
              state="inActive"
              key="priceUsdPctChange"
              label={`Change (${timeFilter.timeDuration})`}
              value={
                <CoinPriceChangePercentageFormatter
                  price={priceChangePct}
                  showArrow={true}
                  className="text-lg"
                />
              }
              caption={`${priceChangePct >= 0 ? "+" : "-"}$${abbreviateNumber(Math.abs(tokenPriceUsd - tokenPriceUsd / (1 + priceChangePct / 100)), { maximumFractionDigits: 4 })} USD`}
              valueClassName={cn("leading-6!", trendingColor)}
              className="border border-border bg-background"
            />
          </div>
          <div className="min-w-[150px] grow basis-[150px]">
            <Metric
              state="inActive"
              key="marketCapUsd"
              label="Total Market Cap"
              value={
                tokenSummary ? (
                  <CoinPriceFormatter
                    price={marketCapUsd}
                    className="text-lg"
                  />
                ) : (
                  <span className="text-lg">N/A</span>
                )
              }
              caption={
                tokenSummary
                  ? `${abbreviateNumber(usdToFocus(marketCapUsd, focusExchangeRate))} ${FOCUS_TICKER_DISPLAY_NAME}`
                  : "N/A"
              }
              valueClassName="leading-6!"
              className="border border-border bg-background"
            />
          </div>
        </div>
      )}
    </div>
  );
};
